import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Loading from "../components/Loading";
import { formatDisplayUserId } from "../utils/userIdFormat";
import { useGetUserProfileQuery, useGetOpenEventsQuery } from "../services/auth/authApi";
import { getRules } from "../data/rules";
import { getDomainAppUrl } from "../utils/domainConfig";

// Platform's own domain (per current hostname) — used across the Terms & Conditions text
const SITE = getDomainAppUrl().toUpperCase();
const RULES = getRules(SITE);

// ─── Icons ────────────────────────────────────────────────────────────────────
const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);
const BadgeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);
const CoinsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <circle cx="8" cy="8" r="6"/><path d="M18.09 10.37A6 6 0 1 1 10.34 18"/><path d="M7 6h1v4"/><path d="m16.71 13.88.7.71-2.82 2.82"/>
  </svg>
);
const PieIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
  </svg>
);
const TrendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
  </svg>
);
const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3">
    <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="white"/>
  </svg>
);
const ScrollIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);

// ─── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, accent, isLoading, onClick }) => {
  const content = (
    <div
      className={`group relative overflow-hidden rounded-2xl p-4 h-full
        bg-white border border-slate-200 shadow-sm
        ${onClick ? "cursor-pointer" : ""}
        transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 text-white shadow-sm"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 6px 16px -6px ${accent}99` }}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider truncate">{label}</p>
          {isLoading ? (
            <div className="h-6 w-20 rounded skeleton mt-1" />
          ) : (
            <p className="text-lg font-extrabold text-slate-800 truncate leading-tight">{value}</p>
          )}
        </div>
      </div>
      {/* bottom accent bar */}
      <span className="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
    </div>
  );

  return onClick ? (
    <div onClick={onClick} className="h-full">{content}</div>
  ) : (
    <div className="h-full">{content}</div>
  );
};

// ─── Open Event Card ───────────────────────────────────────────────────────────
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const OpenEventCard = ({ event }) => {
  const formatEventTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const timeStr = String(hours).padStart(2, "0") + ":" + minutes + " " + ampm;
    if (isToday) return "Today · " + timeStr;
    if (isTomorrow) return "Tomorrow · " + timeStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return day + "-" + month + " · " + timeStr;
  };

  const parts = event.eventName?.split(/\s+v\s+/i) || [];
  const team1 = parts[0] || event.eventName;
  const team2 = parts[1] || null;

  return (
    <Link to={`/dashboard/match/${event.beventId}`} className="group block h-full">
      <div className={`relative overflow-hidden rounded-2xl border h-full flex flex-col transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5
        ${event.isInPlay
          ? "bg-gradient-to-br from-[#0d2b0f] via-[#122f14] to-[#0a2010] border-green-800/60"
          : "bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 border-slate-700/60"
        }`}
      >
        {/* top glow */}
        <div className={`absolute top-0 left-0 right-0 h-0.5 ${event.isInPlay ? "bg-gradient-to-r from-green-500 via-green-300 to-green-500" : "bg-gradient-to-r from-[var(--accent)] via-[var(--primary)] to-[var(--accent)]"}`} />

        <div className="p-4 flex flex-col gap-3 flex-1">
          {/* top row: competition + live badge */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-500 text-xs truncate">{event.competition || "Cricket"}</span>
            {event.isInPlay ? (
              <span className="flex items-center gap-1 bg-green-500/20 text-green-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-500/30 flex-shrink-0">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                LIVE
              </span>
            ) : (
              <span className="flex items-center gap-1 bg-slate-700/50 text-slate-400 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-slate-600/30 flex-shrink-0">
                <CalendarIcon />
                {formatEventTime(event.startTime)}
              </span>
            )}
          </div>

          {/* teams */}
          <div className="flex-1">
            {team2 ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm leading-tight truncate group-hover:text-[var(--accent)] transition-colors">{team1}</p>
                </div>
                <span className="text-gray-500 text-xs font-bold flex-shrink-0">vs</span>
                <div className="flex-1 min-w-0 text-right">
                  <p className="text-white font-semibold text-sm leading-tight truncate group-hover:text-[var(--accent)] transition-colors">{team2}</p>
                </div>
              </div>
            ) : (
              <p className="text-white font-semibold text-sm leading-snug truncate group-hover:text-[var(--accent)] transition-colors">{team1}</p>
            )}
          </div>

          {/* bottom row: time (if live) + clients */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            {event.isInPlay ? (
              <div className="flex items-center gap-1 text-green-400 text-xs">
                <CalendarIcon />
                <span>{formatEventTime(event.startTime)}</span>
              </div>
            ) : (
              <div />
            )}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
              ${(event.totalClients || 0) > 0
                ? "bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/20"
                : "bg-white/5 text-gray-500 border border-white/10"
              }`}
            >
              <UsersIcon />
              <span>{event.totalClients ?? 0} clients</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

// ─── Rules Modal (collapsible Terms & Conditions) ───────────────────────────────
const RulesModal = ({ onClose }) => {
  const [open, setOpen] = useState(() => new Set([1]));
  const toggle = (n) =>
    setOpen((prev) => {
      const s = new Set(prev);
      if (s.has(n)) s.delete(n);
      else s.add(n);
      return s;
    });
  const allOpen = open.size === RULES.length;
  const toggleAll = () =>
    setOpen(allOpen ? new Set() : new Set(RULES.map((r) => r.n)));

  return createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[var(--surface)] text-[var(--text)] border border-[var(--border)] rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col animate-fadeInUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[var(--border)] shrink-0">
          <div>
            <h2 className="text-lg font-bold text-[var(--text)]">Rules &amp; Regulations</h2>
            <p className="text-xs text-[var(--text-muted)]">{SITE} — Terms &amp; Conditions</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleAll}
              className="text-xs font-semibold text-[var(--accent-3)] hover:text-[var(--accent)] whitespace-nowrap"
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </button>
            <button onClick={onClose} className="text-[var(--text-muted)] hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable accordion */}
        <div className="overflow-y-auto px-4 py-3 space-y-2">
          {RULES.map((r) => {
            const isOpen = open.has(r.n);
            return (
              <div key={r.n} className="border border-[var(--border)] rounded-lg overflow-hidden">
                <button
                  onClick={() => toggle(r.n)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left bg-[var(--surface-2)] hover:bg-[var(--surface-3)] transition-colors"
                >
                  <span className="text-sm font-semibold text-[var(--text)]">{r.n}. {r.title}</span>
                  <svg
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className={`w-4 h-4 text-[var(--text-muted)] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {isOpen && (
                  <div className="px-4 py-3 text-xs leading-relaxed text-[#b8bec9] whitespace-pre-line border-t border-[var(--border)]">
                    {r.body}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-[var(--border)] shrink-0">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-[var(--accent)] to-[var(--accent)] text-white py-2.5 rounded-xl font-semibold hover:from-[var(--accent-3)] hover:to-[var(--accent-3)] transition-all shadow-lg shadow-[var(--accent)]/30"
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Dashboard Page ────────────────────────────────────────────────────────────
const DashboardPage = () => {
  const { user } = useSelector((state) => state.auth);
  const { userData, loading, errors } = useSelector((state) => state.dashboard);

  const { data: userProfile, isLoading: userProfileLoading } = useGetUserProfileQuery();
  const { data: openEvents, isLoading: openEventsLoading, error: openEventsError } = useGetOpenEventsQuery();

  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);

  const getUserLevel = (info) => ({
    1: "Client", 2: "Agent", 3: "Super Agent",
    4: "Master", 5: "Mini Admin", 6: "Admin", 7: "Super Admin",
  }[info] || "-");

  const fv = (val, suffix = "") =>
    val === undefined || val === null || val === "" ? "-" : `${val}${suffix}`;

  const isLoadingUserData = loading.userData;

  const username = userProfile?.user?.username
    ?? (userData.userId ? formatDisplayUserId(userData.userId) : null)
    ?? (user?.userId ? formatDisplayUserId(user.userId) : "-");

  const level = userProfile?.user?.userType
    ?? (user?.userTypeInfo ? getUserLevel(user.userTypeInfo) : "-");

  const chips = userProfile?.user?.availableBalance !== undefined
    ? fv(userProfile.user.availableBalance)
    : fv(userData.balance);

  // Company Share = 100 - My Share (user 2026-10-26: My Share 90 -> Company Share 10)
  const invShare = (val) => {
    const n = Number(val);
    return val === undefined || val === null || val === "" || Number.isNaN(n) ? val : +(100 - n).toFixed(2);
  };
  const rawMyShare = userProfile?.user?.shares?.myShare !== undefined
    ? userProfile.user.shares.myShare
    : userData.myShare;
  const myShare = userProfile?.user?.shares?.myShare !== undefined
    ? fv(userProfile.user.shares.myShare, "%")
    : fv(userData.myShare);

  const companyShare = fv(invShare(rawMyShare), "%");

  const matchComm = userProfile?.user?.shares?.matchCommission !== undefined
    ? fv(userProfile.user.shares.matchCommission, "%")
    : fv(userData.matchCommission, "%");

  const sessionComm = userProfile?.user?.shares?.sessionCommission !== undefined
    ? fv(userProfile.user.shares.sessionCommission, "%")
    : fv(userData.sessionCommission, "%");

  const liveCount = openEvents?.data?.filter(e => e.isInPlay).length ?? 0;
  const totalCount = openEvents?.data?.length ?? 0;

  const statCards = [
    { label: "My Share",        value: myShare,      icon: <PieIcon />,    accent: "#6366f1", isLoading: userProfileLoading || isLoadingUserData },
    { label: "Company Share",   value: companyShare, icon: <TrendIcon />,  accent: "#22c55e", isLoading: userProfileLoading || isLoadingUserData },
    { label: "Match Comm.",     value: matchComm,    icon: <TrendIcon />,  accent: "#818cf8", isLoading: userProfileLoading || isLoadingUserData },
    { label: "Session Comm.",   value: sessionComm,  icon: <BookIcon />,   accent: "#16a34a", isLoading: userProfileLoading || isLoadingUserData },
  ];

  return (
    <div className="w-full space-y-5 animate-fadeIn p-2 sm:p-4 max-w-[1400px] mx-auto">

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--accent-4)] via-[var(--accent)] to-[var(--accent-3)] p-6 sm:p-8 shadow-xl animate-fadeInUp">
        {/* decorative */}
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-[var(--primary)]/10 blur-3xl" />
        <div className="absolute -bottom-20 left-1/4 w-64 h-64 rounded-full bg-[var(--primary)]/10 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }}
        />

        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          {/* greeting */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center text-white shrink-0">
              <UserIcon />
            </div>
            <div className="min-w-0">
              <p className="text-[#f9c9dc] text-xs font-medium mb-0.5">Welcome back 👋</p>
              {userProfileLoading ? (
                <div className="h-7 w-44 rounded skeleton" />
              ) : (
                <h1 className="text-white font-extrabold text-2xl sm:text-3xl leading-tight truncate">{username}</h1>
              )}
              <span className="inline-flex items-center gap-1.5 mt-2 bg-white/10 ring-1 ring-white/15 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" /> {level}
              </span>
            </div>
          </div>

          {/* balance + event pills */}
          <div className="flex flex-wrap items-stretch gap-3">
            <div className="rounded-2xl bg-white/10 ring-1 ring-white/15 px-5 py-3 min-w-[150px] flex flex-col justify-center">
              <p className="flex items-center gap-1.5 text-[#f5d5e3] text-[11px] font-medium uppercase tracking-wider">
                <CoinsIcon /> Available Chips
              </p>
              {userProfileLoading ? (
                <div className="h-7 w-24 rounded skeleton mt-1" />
              ) : (
                <p className="text-white font-extrabold text-2xl mt-0.5">{chips}</p>
              )}
            </div>
            <div className="rounded-2xl bg-white/10 ring-1 ring-white/15 px-4 py-3 text-center min-w-[88px] flex flex-col justify-center">
              <p className="text-[#f5d5e3] text-[11px] uppercase tracking-wider">Live</p>
              <p className="text-green-400 font-extrabold text-2xl mt-0.5">{openEventsLoading ? "—" : liveCount}</p>
            </div>
            <div className="rounded-2xl bg-white/10 ring-1 ring-white/15 px-4 py-3 text-center min-w-[88px] flex flex-col justify-center">
              <p className="text-[#f5d5e3] text-[11px] uppercase tracking-wider">Total</p>
              <p className="text-white font-extrabold text-2xl mt-0.5">{openEventsLoading ? "—" : totalCount}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── KPI Cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {statCards.map((card, i) => (
          <div key={card.label} className="animate-row-in" style={{ animationDelay: `${i * 70}ms` }}>
            <StatCard {...card} />
          </div>
        ))}
      </div>

      {errors.userData && (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-600 p-3 text-sm">
          {errors.userData}
        </div>
      )}

      {/* ── Rules Banner ──────────────────────────────────────────────── */}
      <button
        onClick={() => setIsRulesModalOpen(true)}
        className="group w-full flex items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 hover:shadow-md hover:border-[var(--accent)]/40 transition-all text-left animate-fadeInUp"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 text-amber-400 flex items-center justify-center shrink-0">
            <ScrollIcon />
          </div>
          <div>
            <p className="font-bold text-slate-800 text-sm">Rules &amp; Regulations</p>
            <p className="text-slate-400 text-xs">Read platform rules &amp; guidelines</p>
          </div>
        </div>
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] group-hover:translate-x-1 transition-transform">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
      </button>

      {/* ── Open Events ───────────────────────────────────────────────── */}
      <div className="animate-fadeInUp" style={{ animationDelay: "0.2s" }}>
        {/* section header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[var(--accent)] to-[var(--accent-3)] text-white flex items-center justify-center shadow-sm">
              <BadgeIcon />
            </span>
            <h2 className="text-slate-800 font-extrabold text-lg">Open Events</h2>
            {!openEventsLoading && (
              <span className="bg-[var(--accent)]/15 text-[var(--accent)] text-xs font-bold px-2 py-0.5 rounded-full">
                {totalCount}
              </span>
            )}
          </div>
          {liveCount > 0 && (
            <div className="flex items-center gap-1.5 bg-green-500/15 text-green-400 text-xs font-semibold px-2.5 py-1 rounded-full">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              {liveCount} Live
            </div>
          )}
        </div>

        {openEventsLoading ? (
          <div className="flex justify-center items-center py-16 bg-white rounded-2xl border border-slate-200">
            <Loading />
          </div>
        ) : openEventsError ? (
          <div className="rounded-2xl bg-red-50 border border-red-200 text-red-500 text-sm text-center py-10">
            Failed to load events
          </div>
        ) : openEvents?.data?.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {openEvents.data.map((event, i) => (
              <div
                key={event.id ?? event.beventId}
                className="animate-row-in"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <OpenEventCard event={event} />
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-dashed border-slate-300 text-slate-400 text-sm text-center py-14">
            No open events right now
          </div>
        )}
      </div>

      {/* ── Rules Modal ───────────────────────────────────────────────── */}
      {isRulesModalOpen && <RulesModal onClose={() => setIsRulesModalOpen(false)} />}
    </div>
  );
};

export default DashboardPage;
